import {
  File,
  FileArchive,
  FileCode,
  FileImage,
  FileMusic,
  FileSpreadsheet,
  FileText,
  FileVideoCamera,
  Link2,
  type LucideIcon,
  Presentation,
} from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type FileTypeGroup, fileTypeGroupOf, versionShadows } from '@apm/contracts';
import { cn } from '../../lib/utils';

/**
 * Biểu tượng theo định dạng — docs/plan-my-files.md §4.2.
 *
 * Màu theo nhóm quen mắt (Word xanh dương, Excel xanh lá, PowerPoint cam, PDF
 * đỏ) nhưng **không** dùng logo thương hiệu. Nhãn đuôi file đi kèm để màu
 * không phải tín hiệu duy nhất.
 *
 * ⚠️ Bảng lớp viết sẵn thành hằng số: Tailwind quét mã nguồn để biết sinh lớp
 * nào, nên lớp ghép lúc chạy (`text-` + màu) không tồn tại (bẫy APM-1303).
 */
const GROUP_STYLE: Readonly<Record<FileTypeGroup, { icon: LucideIcon; tone: string }>> = {
  image: { icon: FileImage, tone: 'text-fuchsia-600 dark:text-fuchsia-400' },
  video: { icon: FileVideoCamera, tone: 'text-violet-600 dark:text-violet-400' },
  audio: { icon: FileMusic, tone: 'text-pink-600 dark:text-pink-400' },
  document: { icon: FileText, tone: 'text-blue-600 dark:text-blue-400' },
  spreadsheet: { icon: FileSpreadsheet, tone: 'text-green-600 dark:text-green-400' },
  presentation: { icon: Presentation, tone: 'text-orange-600 dark:text-orange-400' },
  pdf: { icon: FileText, tone: 'text-red-600 dark:text-red-400' },
  code: { icon: FileCode, tone: 'text-cyan-700 dark:text-cyan-400' },
  text: { icon: FileText, tone: 'text-slate-600 dark:text-slate-300' },
  archive: { icon: FileArchive, tone: 'text-amber-700 dark:text-amber-400' },
  other: { icon: File, tone: 'text-muted-foreground' },
};

const LINK_STYLE = { icon: Link2, tone: 'text-sky-600 dark:text-sky-400' };

/** Nền của nhãn đuôi — cùng bảng hằng số, cùng lý do. */
const BADGE_TONE: Readonly<Record<FileTypeGroup, string>> = {
  image: 'bg-fuchsia-600',
  video: 'bg-violet-600',
  audio: 'bg-pink-600',
  document: 'bg-blue-600',
  spreadsheet: 'bg-green-600',
  presentation: 'bg-orange-600',
  pdf: 'bg-red-600',
  code: 'bg-cyan-700',
  text: 'bg-slate-600',
  archive: 'bg-amber-700',
  other: 'bg-slate-500',
};

const SIZE_CLASS = { sm: 'size-4', md: 'size-10', lg: 'size-20' } as const;
/** Độ lệch (px) giữa hai lớp bóng phiên bản, theo cỡ biểu tượng. */
const SHADOW_STEP_PX = { sm: 2, md: 4, lg: 7 } as const;
/** Độ đậm của bóng gần → xa. */
const SHADOW_OPACITY = [0.5, 0.28] as const;

/** Đuôi file viết hoa, tối đa 5 ký tự — `null` khi tên không có đuôi. */
export function extensionLabel(name: string): string | null {
  const dot = name.lastIndexOf('.');
  if (dot < 1 || dot === name.length - 1) return null;
  const extension = name.slice(dot + 1);
  return extension.length > 5 ? null : extension.toUpperCase();
}

export function FileTypeIcon({
  name,
  mimeType,
  kind = 'file',
  size = 'sm',
  versions = 1,
  className,
}: {
  readonly name: string;
  readonly mimeType: string | null;
  readonly kind?: 'file' | 'link';
  readonly size?: keyof typeof SIZE_CLASS;
  /**
   * Số phiên bản của tài liệu (APM-MYF-n): mỗi phiên bản trước là một bóng của **biểu tượng**
   * xếp sau nó (nhãn đuôi tệp không có bóng), tối đa 2 bóng — `versionShadows`.
   */
  readonly versions?: number;
  readonly className?: string;
}): ReactNode {
  const { t } = useTranslation();
  const group = fileTypeGroupOf(name, mimeType);
  const style = kind === 'link' ? LINK_STYLE : GROUP_STYLE[group];
  const Icon = style.icon;
  const label = kind === 'link' || size === 'sm' ? null : extensionLabel(name);
  const shadows = kind === 'link' ? 0 : versionShadows(versions);
  const step = SHADOW_STEP_PX[size];

  return (
    <span
      className={cn('relative inline-flex shrink-0', className)}
      data-file-group={kind === 'link' ? 'link' : group}
      data-version-shadows={shadows || undefined}
    >
      {/* Bóng xa trước, gần sau — lớp sau che nét của lớp trước (tô nền), như giấy xếp chồng. */}
      {Array.from({ length: shadows }, (_, index) => shadows - index).map((depth) => (
        <Icon
          key={depth}
          aria-hidden
          data-testid="version-shadow"
          className={cn('absolute left-0 top-0 fill-background', SIZE_CLASS[size], style.tone)}
          style={{
            transform: `translate(${depth * step}px, ${-depth * step}px)`,
            opacity: SHADOW_OPACITY[depth - 1],
          }}
          strokeWidth={1.5}
        />
      ))}
      <Icon
        aria-hidden
        className={cn('relative', SIZE_CLASS[size], style.tone, shadows > 0 && 'fill-background')}
        strokeWidth={1.5}
      />
      {versions > 1 ? (
        // Dấu phẩy: chữ ẩn đứng ngay trước tên tệp trong tên đọc của dòng — "3 phiên bản, Báo cáo.md".
        <span className="sr-only">{`${t('myFiles.versions.count', { count: versions })}, `}</span>
      ) : null}
      {label ? (
        <span
          aria-hidden
          className={cn(
            'absolute -bottom-0.5 left-1/2 -translate-x-1/2 rounded-sm px-1 font-semibold leading-tight text-white',
            size === 'lg' ? 'text-[11px]' : 'text-[9px]',
            BADGE_TONE[group],
          )}
        >
          {label}
        </span>
      ) : null}
    </span>
  );
}
